import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from './firebase';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from "firebase/auth";
import config_names from './config_names';
import { 
  FiMail, 
  FiAlertCircle, 
  FiArrowLeft,
  FiCheckCircle
} from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import './auth-pages.css';

const getErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'The email address is not valid. Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'No user found with this email. Please sign up.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-credential':
      return 'Invalid credentials. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait and try again.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

const getResetErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'The email address is not valid. Please enter a valid email address.';
    case 'auth/missing-email':
      return 'Please enter your account email address.';
    case 'auth/too-many-requests':
      return 'Too many reset attempts. Please wait and try again.';
    default:
      return 'Unable to send reset email right now. Please try again.';
  }
};

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    setError(null);
    setSuccess(null);
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider)
      .then((result) => {
        navigate('/dashboard');
      })
      .catch((error) => {
        setError(getErrorMessage(error.code));
      });
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (error) {
      setError(getErrorMessage(error.code));
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('If an account exists for this email, a reset link has been sent.');
    } catch (error) {
      setError(getResetErrorMessage(error.code));
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Welcome Back</h1>
          <p>Sign in to continue to {config_names.projectName}</p>
        </div>

        {error && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>{success}</span>
          </div>
        )}

        {!showEmailLogin ? (
          <>
            <button 
              className="auth-btn google-btn" 
              onClick={handleGoogleLogin}
            >
              <FaGoogle /> Sign in with Google
            </button>
            
            <div className="auth-divider">or</div>
            
            <button 
              className="auth-btn primary-btn" 
              onClick={() => setShowEmailLogin(true)}
            >
              <FiMail /> Sign in with Email
            </button>
          </>
        ) : showForgotPassword ? (
          <form className="auth-form" onSubmit={handleForgotPassword}>
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <button type="submit" className="auth-btn primary-btn">
              <FiMail /> Send reset link
            </button>

            <button
              type="button"
              className="auth-btn secondary-btn"
              onClick={() => {
                setShowForgotPassword(false);
                setError(null);
                setSuccess(null);
              }}
            >
              <FiArrowLeft /> Back to sign in
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleLogin}>
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="email" 
                  className="form-control" 
                  placeholder="Email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="password" 
                  className="form-control" 
                  placeholder="Password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                />
              </div>
            </div>

            <button
              type="button"
              className="auth-inline-link"
              onClick={() => {
                setShowForgotPassword(true);
                setError(null);
                setSuccess(null);
              }}
            >
              Forgot password?
            </button>
            
            <button type="submit" className="auth-btn primary-btn">
              <FiMail /> Sign in
            </button>
            
            <button 
              type="button" 
              className="auth-btn secondary-btn" 
              onClick={() => {
                setShowEmailLogin(false);
                setShowForgotPassword(false);
                setError(null);
                setSuccess(null);
              }}
            >
              <FiArrowLeft /> Back
            </button>
          </form>
        )}

        <div className="auth-footer">
          <span>New to {config_names.projectName}? </span>
          <Link to="/signup">Sign up now</Link>
        </div>

        <div className="auth-legal">
          By continuing, you acknowledge that you accept {config_names.projectName_genitive}{' '}
          <a href={`${config_names.domain}/terms`}>Terms of Use</a> and{' '}
          <a href={`${config_names.domain}/privacy`}>Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
